<script setup lang="ts">
import { computed, ComputedRef, defineProps, onBeforeUnmount, onMounted, Ref, ref, watch } from 'vue';
import { Draft, DraftType } from '@/dashboard/types/draft';
import PlayIcon from '@/dashboard/vue/components/icons/PlayIcon.vue';
import LoaderIcon from '@/dashboard/vue/components/icons/LoaderIcon.vue';
import { DraftsApi } from '@/dashboard/api/drafts';
import { useImageChecker } from '@/dashboard/vue/components/drafts/DraftCard/useImageChecker';

const props = defineProps<{
  draft: Draft;
}>();

let intervalId: ReturnType<typeof setInterval> | null = null;
const loadedImageLink: Ref<string | undefined> = ref(undefined);
const imageLink = computed(() => loadedImageLink.value ?? props.draft.image?.thumb_url);
const originalImageLink = computed(() => loadedImageLink.value ?? props.draft.image?.original_url);
const { finalUrl } = useImageChecker(imageLink, originalImageLink);
const showPreview = ref(false);

async function getDraft() {
  const response = await DraftsApi.get(props.draft.id);
  if (response.draft && response.draft.image?.original_url) {
    loadedImageLink.value = response.draft.image.original_url;

    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }
}

watch(imageLink, (value) => {
  if (value) {
    setTimeout(() => (showPreview.value = true), 10000);
  }
});

onMounted(async () => {
  if (!imageLink.value) {
    await getDraft();
    intervalId = setInterval(getDraft, 10000);
  } else {
    showPreview.value = true;
  }
});

onBeforeUnmount(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});
</script>

<template>
  <div class="flex h-full w-full items-center justify-center">
    <img
      v-if="showPreview"
      :src="finalUrl"
      :class="{ 'rounded-4': draft.draft_type === DraftType.image, 'rounded-24': draft.draft_type === DraftType.video }"
      :alt="draft.name"
    />
    <play-icon
      v-if="draft.draft_type === DraftType.video && showPreview"
      class="absolute h-[40px] w-[40px] fill-white drop-shadow-[0px_6px_16px_rgba(0,0,0,0.05)] dark:fill-gray-800"
    />

    <LoaderIcon v-if="!showPreview" class="animate-spin stroke-black dark:stroke-white" />
  </div>
</template>
